import { useEffect, useMemo, useState } from "react";
import DSBox from "../../system-design/DSBox";
import DSTypography from "../../system-design/DSTypography";
import DSTextField from "../../system-design/DSTextField";
import DSMenuItem from "../../system-design/DSMenuItem";
import DSPrimaryButton from "../../system-design/DSPrimaryButton";
import DSSecondaryButton from "../../system-design/DSSecondaryButton";
import DSChip from "../../system-design/DSChip";
import DSCircularProgress from "../../system-design/DSCircularProgress";
import KlydosByIdsTable from "./KlydosByIdsTable";
import CreateFirmwareKlydosModal from "./CreateFirmwareKlydosModal";
import ReorderKlydosDialog from "./ReorderKlydosDialog";
import KlydosPickDialog from "../../Gifts/GiftCategories/components/KlydosPickDialog";
import useFirmwareKlydosQuery from "../hooks/useFirmwareKlydosQuery";
import useUpdateFirmwareKlydosMutation from "../hooks/useUpdateFirmwareKlydosMutation";
import useGetKlydosByIds from "../../Device/hooks/useGetKlydosByIds";
import useDownloadFirmwareKlydosLoops from "../hooks/useDownloadFirmwareKlydosLoops";
import useFirmwareArtists from "../hooks/useFirmwareArtists";
import {
  buildFirmwareKlydosXml,
  downloadXml,
} from "../utils/firmwareKlydosXml";
import orderKlydosByIds from "../utils/orderKlydosByIds";
import { FirmwareKlydos as FirmwareKlydosType } from "../../../api/firmware-klydos";

const firmwareLabel = (fw: FirmwareKlydosType): string =>
  fw.name?.trim() ? fw.name : fw.id;

const FirmwareKlydos = () => {
  const { data: firmwares = [], isLoading } = useFirmwareKlydosQuery();
  const updateMutation = useUpdateFirmwareKlydosMutation();

  const [selectedId, setSelectedId] = useState<string>("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showReorderModal, setShowReorderModal] = useState(false);

  // Default to the most-recent firmware (the list arrives sorted desc). Keep the
  // current selection if it still exists after a refetch.
  useEffect(() => {
    if (firmwares.length === 0) {
      setSelectedId("");
      return;
    }
    setSelectedId((prev) =>
      prev && firmwares.some((fw) => fw.id === prev) ? prev : firmwares[0].id,
    );
  }, [firmwares]);

  const selectedFirmware = useMemo(
    () => firmwares.find((fw) => fw.id === selectedId),
    [firmwares, selectedId],
  );

  // Shares the query key with the table below, so this doesn't double-fetch.
  const { klydos: selectedKlydos = [], isLoading: klydosLoading } =
    useGetKlydosByIds(selectedFirmware?.klydosIds ?? []);

  const handleReorderSave = async (klydosIds: string[]) => {
    if (!selectedFirmware) return;
    await updateMutation.mutateAsync({ id: selectedFirmware.id, klydosIds });
    setShowReorderModal(false);
  };

  const handleEditSave = async (klydosIds: string[]) => {
    if (!selectedFirmware) return;
    await updateMutation.mutateAsync({ id: selectedFirmware.id, klydosIds });
    setShowEditModal(false);
  };

  const handleExportXml = () => {
    if (!selectedFirmware) return;
    // The firmware's klydosIds order is the export order; the by-ids fetch
    // returns whatever order the API chose.
    const xml = buildFirmwareKlydosXml(
      orderKlydosByIds(selectedKlydos, selectedFirmware.klydosIds),
    );
    downloadXml(`${firmwareLabel(selectedFirmware)}.xml`, xml);
  };

  const { downloadLoops, isDownloading, progress } =
    useDownloadFirmwareKlydosLoops();

  const handleDownloadKlydos = () => {
    if (!selectedFirmware) return;
    downloadLoops(
      orderKlydosByIds(selectedKlydos, selectedFirmware.klydosIds),
      firmwareLabel(selectedFirmware),
    );
  };

  const {
    exportArtistsXml,
    downloadArtistImages,
    isExportingXml,
    isDownloadingImages,
    progress: artistsProgress,
  } = useFirmwareArtists();

  const handleExportArtistsXml = () => {
    if (!selectedFirmware) return;
    exportArtistsXml(
      orderKlydosByIds(selectedKlydos, selectedFirmware.klydosIds),
      firmwareLabel(selectedFirmware),
    );
  };

  const handleDownloadArtistImages = () => {
    if (!selectedFirmware) return;
    downloadArtistImages(
      orderKlydosByIds(selectedKlydos, selectedFirmware.klydosIds),
      firmwareLabel(selectedFirmware),
    );
  };

  const artistsBusy = isExportingXml || isDownloadingImages;

  if (isLoading) {
    return <DSCircularProgress />;
  }

  return (
    <>
      <DSBox
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
          mb: 2,
        }}
      >
        <DSBox display="flex" alignItems="center" gap={1}>
          <DSTypography variant="caption">Firmware:</DSTypography>
          <DSTextField
            select
            size="small"
            sx={{ minWidth: 320 }}
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            disabled={firmwares.length === 0}
          >
            {firmwares.length === 0 ? (
              <DSMenuItem value="">No firmwares yet</DSMenuItem>
            ) : (
              firmwares.map((fw) => (
                <DSMenuItem key={fw.id} value={fw.id}>
                  {firmwareLabel(fw)}
                </DSMenuItem>
              ))
            )}
          </DSTextField>
          {selectedFirmware?.sealed && (
            <DSChip label="Sealed" size="small" color="warning" />
          )}
          {selectedFirmware && (
            <DSTypography variant="caption" color="text.secondary">
              {selectedFirmware.klydosIds.length} klydos
            </DSTypography>
          )}
        </DSBox>

        <DSBox sx={{ display: "flex", gap: 1, ml: "auto" }}>
          {selectedFirmware && (
            <DSSecondaryButton
              size="small"
              onClick={handleExportXml}
              disabled={klydosLoading || selectedKlydos.length === 0}
            >
              Export to XML
            </DSSecondaryButton>
          )}
          {selectedFirmware && (
            <DSSecondaryButton
              size="small"
              onClick={handleDownloadKlydos}
              disabled={
                klydosLoading || selectedKlydos.length === 0 || isDownloading
              }
            >
              {isDownloading
                ? `Downloading… ${progress.done}/${progress.total}`
                : "Download klydos"}
            </DSSecondaryButton>
          )}
          {selectedFirmware && (
            <DSSecondaryButton
              size="small"
              onClick={handleExportArtistsXml}
              disabled={
                klydosLoading || selectedKlydos.length === 0 || artistsBusy
              }
            >
              {isExportingXml
                ? `Artists XML… ${artistsProgress.done}/${artistsProgress.total}`
                : "Export artists XML"}
            </DSSecondaryButton>
          )}
          {selectedFirmware && (
            <DSSecondaryButton
              size="small"
              onClick={handleDownloadArtistImages}
              disabled={
                klydosLoading || selectedKlydos.length === 0 || artistsBusy
              }
            >
              {isDownloadingImages
                ? `Artist images… ${artistsProgress.done}/${artistsProgress.total}`
                : "Download artist images"}
            </DSSecondaryButton>
          )}
          {selectedFirmware && !selectedFirmware.sealed && (
            <DSSecondaryButton
              size="small"
              onClick={() => setShowEditModal(true)}
            >
              Edit klydos
            </DSSecondaryButton>
          )}
          {selectedFirmware && !selectedFirmware.sealed && (
            <DSSecondaryButton
              size="small"
              onClick={() => setShowReorderModal(true)}
              disabled={selectedFirmware.klydosIds.length < 2}
            >
              Edit order
            </DSSecondaryButton>
          )}
          <DSPrimaryButton
            size="small"
            onClick={() => setShowCreateModal(true)}
          >
            Create new firmware klydos
          </DSPrimaryButton>
        </DSBox>
      </DSBox>

      {selectedFirmware ? (
        <KlydosByIdsTable
          // Re-key on the firmware so the table (and its internal search/scroll
          // state) resets cleanly when switching firmwares.
          key={selectedFirmware.id}
          klydosIds={selectedFirmware.klydosIds}
          onReorder={
            selectedFirmware.sealed
              ? undefined
              : (klydosIds) =>
                  updateMutation.mutate({ id: selectedFirmware.id, klydosIds })
          }
        />
      ) : (
        <DSBox sx={{ textAlign: "center", p: 4 }}>
          <DSTypography color="text.secondary">
            No firmware klydos yet. Create one to get started.
          </DSTypography>
        </DSBox>
      )}

      {showCreateModal && (
        <CreateFirmwareKlydosModal
          onClose={() => setShowCreateModal(false)}
          onCreated={(created) => setSelectedId(created.id)}
        />
      )}

      {showReorderModal && selectedFirmware && (
        <ReorderKlydosDialog
          title={`Edit order — ${firmwareLabel(selectedFirmware)}`}
          description="The XML export follows this order."
          klydosIds={selectedFirmware.klydosIds}
          onSave={handleReorderSave}
          onClose={() => setShowReorderModal(false)}
          isSaving={updateMutation.isPending}
        />
      )}

      {showEditModal && selectedFirmware && (
        <KlydosPickDialog
          title={`Edit klydos — ${firmwareLabel(selectedFirmware)}`}
          saveLabel="Save"
          selectedKlydos={selectedFirmware.klydosIds}
          onPickKlydos={handleEditSave}
          onClose={() => setShowEditModal(false)}
        />
      )}
    </>
  );
};

export default FirmwareKlydos;
